/**
 * User Detail Modal Component
 * Displays comprehensive excessive access insight for a specific user
 */

import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Grid2 from '@mui/material/Unstable_Grid2';
import { X, User, AlertCircle, TrendingUp, Shield, FileText, Download } from 'lucide-react';

const UserDetailModal = ({ show, onHide, user }) => {
  if (!user) return null;

  const getClassificationColor = (classification) => {
    const colors = {
      CRITICAL_EXCESS: '#dc3545',
      HIGH_EXCESS: '#fd7e14',
      MODERATE_EXCESS: '#ffc107',
      ACCEPTABLE: '#28a745'
    };
    return colors[classification] || '#6c757d';
  };

  const getImpactColor = (impact) => {
    const variants = {
      high: 'error',
      medium: 'warning',
      low: 'success'
    };
    return variants[impact] || 'default';
  };

  return (
    <Dialog open={show} onClose={onHide} maxWidth="xl" fullWidth scroll="paper">
      <DialogTitle sx={{ bgcolor: 'background.default', display: 'flex', alignItems: 'center' }}>
        <User size={24} style={{ marginRight: 8 }} />
        Excessive Access Insight
      </DialogTitle>

      <DialogContent dividers>
        {/* User Info */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Grid2 container spacing={2}>
              <Grid2 xs={12} md={6}>
                <Typography variant="h5">{user.display_name || user.user_email}</Typography>
                <Typography color="text.secondary" sx={{ mb: 1 }}>{user.user_email}</Typography>
                <Typography sx={{ mb: 0.5 }}>
                  <strong>Department:</strong> {user.department || 'N/A'}
                </Typography>
                <Typography>
                  <strong>Job Title:</strong> {user.job_title || 'N/A'}
                </Typography>
              </Grid2>
              <Grid2 xs={12} md={6} sx={{ textAlign: { md: 'right' } }}>
                <Chip
                  label={user.classification?.replace('_', ' ')}
                  sx={{
                    fontSize: '1.2rem',
                    px: 2,
                    py: 1,
                    height: 'auto',
                    bgcolor: getClassificationColor(user.classification),
                    color: '#fff'
                  }}
                />
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h4" sx={{ mb: 0 }}>Risk Score: {user.risk_score?.toFixed(1)}</Typography>
                  <Typography color="text.secondary">
                    Confidence: {(user.confidence_score * 100)?.toFixed(0)}%
                  </Typography>
                </Box>
              </Grid2>
            </Grid2>
          </CardContent>
        </Card>

        {/* Peer Cohort Comparison */}
        <Card sx={{ mb: 4 }}>
          <Box sx={{ px: 2, py: 1.5, bgcolor: 'background.default', display: 'flex', alignItems: 'center' }}>
            <TrendingUp size={20} style={{ marginRight: 8 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Peer Cohort Comparison</Typography>
          </Box>
          <CardContent>
            <Box sx={{ mb: 3 }}>
              <Typography component="span"><strong>Cohort:</strong> {user.peer_cohort_name}</Typography>
              <Chip
                label={`${user.peer_cohort_size} members`}
                size="small"
                sx={{ ml: 1 }}
              />
            </Box>

            <Table size="small" sx={{ border: '1px solid', borderColor: 'divider' }}>
              <TableHead sx={{ bgcolor: 'background.default' }}>
                <TableRow>
                  <TableCell>Metric</TableCell>
                  <TableCell>User Count</TableCell>
                  <TableCell>Peer Average</TableCell>
                  <TableCell>Std Dev</TableCell>
                  <TableCell>Z-Score</TableCell>
                  <TableCell>Percentile</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell><strong>Groups</strong></TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{user.user_group_count}</TableCell>
                  <TableCell>{user.peer_avg_group_count?.toFixed(1)}</TableCell>
                  <TableCell>{user.peer_stddev_group_count?.toFixed(1)}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.group_count_z_score?.toFixed(2)}
                      color={Math.abs(user.group_count_z_score) >= 2 ? 'error' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{user.group_count_percentile}th</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Applications</strong></TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{user.user_app_count}</TableCell>
                  <TableCell>{user.peer_avg_app_count?.toFixed(1)}</TableCell>
                  <TableCell>{user.peer_stddev_app_count?.toFixed(1)}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.app_count_z_score?.toFixed(2)}
                      color={Math.abs(user.app_count_z_score) >= 2 ? 'error' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>-</TableCell>
                </TableRow>
                {user.user_privileged_count > 0 && (
                  <TableRow>
                    <TableCell><strong>Privileged Access</strong></TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{user.user_privileged_count}</TableCell>
                    <TableCell>{user.peer_avg_privileged_count?.toFixed(1)}</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>
                      <Chip
                        label={user.privileged_z_score?.toFixed(2)}
                        color={Math.abs(user.privileged_z_score) >= 2 ? 'error' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Unused Access */}
        <Card sx={{ mb: 4 }}>
          <Box sx={{ px: 2, py: 1.5, bgcolor: 'background.default', display: 'flex', alignItems: 'center' }}>
            <AlertCircle size={20} style={{ marginRight: 8 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Unused Access Analysis</Typography>
          </Box>
          <CardContent>
            <Grid2 container spacing={2} sx={{ mb: 3 }}>
              <Grid2 xs={12} md={4}>
                <Box sx={{ textAlign: 'center', p: 3, bgcolor: '#ffe5e5', borderRadius: 2 }}>
                  <Typography variant="h4" sx={{ mb: 0 }}>{user.unused_access_percentage?.toFixed(0)}%</Typography>
                  <Typography color="text.secondary">Unused Access</Typography>
                </Box>
              </Grid2>
              <Grid2 xs={12} md={4}>
                <Box sx={{ textAlign: 'center', p: 3, bgcolor: '#fff3e0', borderRadius: 2 }}>
                  <Typography variant="h4" sx={{ mb: 0 }}>{user.unused_group_count}</Typography>
                  <Typography color="text.secondary">Unused Groups</Typography>
                </Box>
              </Grid2>
              <Grid2 xs={12} md={4}>
                <Box sx={{ textAlign: 'center', p: 3, bgcolor: '#fffaed', borderRadius: 2 }}>
                  <Typography variant="h4" sx={{ mb: 0 }}>{user.unused_app_count}</Typography>
                  <Typography color="text.secondary">Unused Apps</Typography>
                </Box>
              </Grid2>
            </Grid2>

            {user.unused_resource_names && user.unused_resource_names.length > 0 && (
              <>
                <Typography sx={{ mb: 1 }}><strong>Unused Resources:</strong></Typography>
                <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
                  <List dense disablePadding>
                    {user.unused_resource_names.slice(0, 10).map((resource, idx) => (
                      <React.Fragment key={idx}>
                        <ListItem>
                          <ListItemText primary={resource} primaryTypographyProps={{ fontSize: '0.9rem' }} />
                        </ListItem>
                        {idx < Math.min(user.unused_resource_names.length, 10) - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                    {user.unused_resource_names.length > 10 && (
                      <>
                        <Divider />
                        <ListItem>
                          <ListItemText
                            primary={`+ ${user.unused_resource_names.length - 10} more...`}
                            primaryTypographyProps={{ fontSize: '0.9rem', color: 'text.secondary' }}
                          />
                        </ListItem>
                      </>
                    )}
                  </List>
                </Box>
              </>
            )}
          </CardContent>
        </Card>

        {/* AI Analysis */}
        <Card sx={{ mb: 4 }}>
          <Box sx={{ px: 2, py: 1.5, bgcolor: 'background.default', display: 'flex', alignItems: 'center' }}>
            <Shield size={20} style={{ marginRight: 8 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>AI Analysis</Typography>
          </Box>
          <CardContent>
            {user.reasoning && (
              <>
                <Typography variant="subtitle2">Reasoning:</Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>{user.reasoning}</Typography>
              </>
            )}

            {user.root_causes && user.root_causes.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2 }}>Root Causes:</Typography>
                <ul>
                  {user.root_causes.map((cause, idx) => (
                    <li key={idx}>{cause}</li>
                  ))}
                </ul>
              </>
            )}

            {user.recommendations && user.recommendations.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2 }}>Recommendations:</Typography>
                <ol>
                  {user.recommendations.map((rec, idx) => (
                    <li key={idx}>{rec}</li>
                  ))}
                </ol>
              </>
            )}
          </CardContent>
        </Card>

        {/* Priority Resources */}
        {user.priority_resources && user.priority_resources.length > 0 && (
          <Card sx={{ mb: 4 }}>
            <Box sx={{ px: 2, py: 1.5, bgcolor: 'background.default', display: 'flex', alignItems: 'center' }}>
              <FileText size={20} style={{ marginRight: 8 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Priority Resources to Review</Typography>
            </Box>
            <CardContent>
              <List disablePadding>
                {user.priority_resources.map((resource, idx) => (
                  <React.Fragment key={idx}>
                    <ListItem sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <ListItemText
                        primary={<strong>{resource.resource_name || resource.name}</strong>}
                        secondary={resource.reason || resource.description}
                        secondaryTypographyProps={{ fontSize: '0.9rem' }}
                      />
                      <Chip label="Review" color="warning" size="small" />
                    </ListItem>
                    {idx < user.priority_resources.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        )}

        {/* Impact Assessment */}
        <Card sx={{ mb: 4 }}>
          <Box sx={{ px: 2, py: 1.5, bgcolor: 'background.default', display: 'flex', alignItems: 'center' }}>
            <AlertCircle size={20} style={{ marginRight: 8 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Impact Assessment</Typography>
          </Box>
          <CardContent>
            <Grid2 container spacing={2}>
              <Grid2 xs={12} md={4}>
                <Typography sx={{ mb: 1 }}>
                  <strong>Business Impact:</strong>{' '}
                  <Chip
                    label={user.business_impact || 'medium'}
                    color={getImpactColor(user.business_impact)}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Typography>
              </Grid2>
              <Grid2 xs={12} md={4}>
                <Typography sx={{ mb: 1 }}>
                  <strong>Security Risk:</strong>{' '}
                  <Chip
                    label={user.security_risk || 'medium'}
                    color={getImpactColor(user.security_risk)}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Typography>
              </Grid2>
              <Grid2 xs={12} md={4}>
                <Typography sx={{ mb: 1 }}>
                  <strong>Compliance Impact:</strong>{' '}
                  <Chip
                    label={user.compliance_impact || 'medium'}
                    color={getImpactColor(user.compliance_impact)}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Typography>
              </Grid2>
            </Grid2>
          </CardContent>
        </Card>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onHide} startIcon={<X size={16} />}>
          Close
        </Button>
        <Button variant="contained" startIcon={<Download size={16} />}>
          Export Report
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserDetailModal;
